
import { supabase } from '@/integrations/supabase/client';
import { BlogPost } from '@/types/blog';

/**
 * Format date to a consistent readable format
 * @param dateString Date string to format
 * @returns Formatted date string
 */
const formatDateString = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn(`[Blog] Invalid date string: "${dateString}"`);
      return '';
    }
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });
  } catch (error) {
    console.error(`[Blog] Error formatting date string "${dateString}":`, error);
    return '';
  }
};

/**
 * Get all blog posts from the database or fallback to static data
 * @param publishedOnly Whether to return only published posts
 * @returns Array of blog posts
 */
export const getAllBlogPosts = async (publishedOnly: boolean = true): Promise<BlogPost[]> => {
  try {
    console.log("[Blog Utils] Fetching blog posts from Supabase with publishedOnly=", publishedOnly);
    
    let query = supabase.from('blog_posts').select('*');
    
    if (publishedOnly) {
      query = query.eq('is_published', true);
    }
    
    const { data: posts, error } = await query.order('publish_date', { ascending: false });
    
    if (error) {
      console.error("[Blog Utils] Error fetching posts from Supabase:", error);
      throw error;
    }
    
    if (!posts || posts.length === 0) {
      console.log("[Blog Utils] No posts found in Supabase");
      return [];
    }
    
    console.log(`[Blog Utils] Found ${posts.length} posts in Supabase`);
    
    // Process each post to get its tags
    const formattedPosts: BlogPost[] = await Promise.all(
      posts.map(async (post) => {
        try {
          // Get tags for this post
          const { data: tagsData, error: tagsError } = await supabase
            .from('blog_post_tags')
            .select('tag_id')
            .eq('post_id', post.id);
            
          if (tagsError) {
            console.error(`[Blog Utils] Error fetching tags for post ${post.id}:`, tagsError);
            throw tagsError;
          }
          
          let tags: string[] = [];
          if (tagsData && tagsData.length > 0) {
            try {
              // Get the tag names
              const tagIds = tagsData.map(t => t.tag_id);
              
              const { data: tagNames, error: tagNamesError } = await supabase
                .from('blog_tags')
                .select('name')
                .in('id', tagIds);
                
              if (tagNamesError) {
                console.error(`[Blog Utils] Error fetching tag names for post ${post.id}:`, tagNamesError);
                throw tagNamesError;
              }
              
              tags = tagNames?.map(t => t.name) || [];
            } catch (tagError) {
              console.error(`[Blog Utils] Error processing tags for post ${post.id}:`, tagError);
              // Continue with empty tags rather than failing
            }
          }
          
          // Format the post
          return {
            id: post.id,
            title: post.title || 'Untitled',
            slug: post.slug,
            excerpt: post.post_summary || '',
            content: post.content || '',
            coverImage: post.featured_image,
            date: formatDateString(post.publish_date),
            tags: tags,
            author: post.author || 'edgefi team',
            author_id: post.author_id,
            isPublished: post.is_published
          };
        } catch (postError) {
          console.error(`[Blog Utils] Error processing post ${post.id}:`, postError);
          // Return a minimal valid post instead of failing the whole operation
          return {
            id: post.id,
            title: post.title || 'Error: Failed to process post',
            slug: post.slug,
            content: '',
            isPublished: post.is_published || false
          };
        }
      })
    );
    
    return formattedPosts;
  } catch (error) {
    console.error("[Blog Utils] Error in getAllBlogPosts:", error);
    throw error;
  }
};

/**
 * Get a specific blog post by slug
 * @param slug The post slug
 * @param publishedOnly Whether to return only published posts
 * @returns The blog post, or null if not found
 */
export const getBlogPostBySlug = async (slug: string, publishedOnly: boolean = true): Promise<BlogPost | null> => {
  try {
    console.log(`[Blog Utils] Fetching blog post with slug: ${slug}, publishedOnly: ${publishedOnly}`);
    
    const { data: post, error } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('slug', slug)
      .maybeSingle();
      
    if (error) {
      console.error(`[Blog Utils] Error fetching post with slug ${slug}:`, error);
      throw error;
    }
    
    if (!post) {
      console.log(`[Blog Utils] No post found for slug: ${slug}`);
      return null;
    }
    
    // If publishedOnly is true and post is not published, return null
    if (publishedOnly && !post.is_published) {
      console.log(`[Blog Utils] Post with slug ${slug} is not published, returning null`);
      return null;
    }

    console.log(`[Blog Utils] Found post with slug ${slug}:`, post.title);

    // Get tags for this post with error handling
    let tags: string[] = [];
    try {
      const { data: tagsData, error: tagsError } = await supabase
        .from('blog_post_tags')
        .select('tag_id')
        .eq('post_id', post.id);
        
      if (tagsError) {
        console.error(`[Blog Utils] Error fetching tags for post ${post.id}:`, tagsError);
      } else if (tagsData && tagsData.length > 0) {
        // Get the tag names
        const tagIds = tagsData.map(t => t.tag_id);
        
        const { data: tagNames, error: tagNamesError } = await supabase
          .from('blog_tags')
          .select('name')
          .in('id', tagIds);
          
        if (tagNamesError) {
          console.error(`[Blog Utils] Error fetching tag names for post ${post.id}:`, tagNamesError);
        } else {
          tags = tagNames?.map(t => t.name) || [];
        }
      }
    } catch (tagError) {
      console.error("[Blog Utils] Error fetching tags for post:", tagError);
      // Continue with empty tags rather than failing the whole request
    }
    
    // Get image attribution if available
    let attribution = null;
    try {
      const { data: attributionData, error: attributionError } = await supabase
        .from('blog_image_attribution')
        .select('photographer, photographer_url')
        .eq('post_id', post.id)
        .maybeSingle();
      
      if (attributionError) {
        console.error(`[Blog Utils] Error fetching image attribution for post ${post.id}:`, attributionError);
      } else if (attributionData) {
        attribution = {
          photographer: attributionData.photographer,
          photographerUrl: attributionData.photographer_url
        };
      }
    } catch (attrError) {
      console.error(`[Blog Utils] Error processing attribution for post ${post.id}:`, attrError);
    }
    
    // Format the post data
    const formattedPost: BlogPost = {
      id: post.id,
      title: post.title || 'Untitled',
      slug: post.slug,
      excerpt: post.post_summary || '',
      content: post.content || '',
      coverImage: post.featured_image,
      date: formatDateString(post.publish_date),
      tags: tags,
      author: post.author || 'edgefi team',
      author_id: post.author_id,
      isPublished: post.is_published,
      imageAttribution: attribution,
      metaDescription: post.meta_description
    };
    
    return formattedPost;
  } catch (error) {
    console.error(`[Blog Utils] Error in getBlogPostBySlug for slug ${slug}:`, error);
    throw error;
  }
};
